// Import the necessary modules and components
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ChangePassword from "./ChangePassword";
import { useAuthContext } from "../contexts/AuthContext";
import logo from '../assets/logo.png';
import background from '../assets/background.jpg';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loginBlocked, setLoginBlocked] = useState(false);
  const [loginError, setLoginError] = useState(null); // Add state for login error
  const [timeoutMessage, setTimeoutMessage] = useState(""); // Add state for timeout message
  const navigate = useNavigate();
  const { authenticate } = useAuthContext();
  const loginAttempts = useRef(0);

  const initiateLogin = async () => {
    if (loginBlocked) {
      console.error("Login blocked. Too many attempts.");
      return;
    }

    try {
      if (loginAttempts.current >= 3) {
        let timeoutDuration = 30000; // Default timeout: 30 seconds

        if (loginAttempts.current >= 10 && loginAttempts.current < 20) {
          timeoutDuration = 300000; // 5 minutes for 10-19 attempts
        } else if (loginAttempts.current >= 20) {
          timeoutDuration = 600000; // 10 minutes for 20 or more attempts
        }

        const timeoutSeconds = timeoutDuration / 1000;

        console.error(`Too many login attempts. Blocking login for ${timeoutSeconds} seconds.`);
        setLoginBlocked(true);
        setTimeoutMessage(`Please try again in: ${timeoutSeconds} seconds.`);

        setTimeout(() => {
          setLoginBlocked(false);
          loginAttempts.current = 0;
          setTimeoutMessage(""); // Clear timeout message after the timeout period
        }, timeoutDuration);
        return;
      }

      const { newChallengeRequired, session } = await authenticate(email, password);

      if (newChallengeRequired) {
        setShowChangePassword(true);
      } else {
        loginAttempts.current = 0;
        navigate(`/dashboard`);
      }
    } catch (error) {
      console.error("Login failed: ", error);
      loginAttempts.current += 1;

      if (error.code === "PasswordResetRequiredException") {
        setShowChangePassword(true);
      } else {
        setLoginError("Invalid username or password. Please try again.");

        setTimeout(() => {
          setLoginError(null);
        }, 5000); // Clear error after 5 seconds

        if (loginAttempts.current >= 3) {
          let timeoutDuration = 30000; // Default timeout: 30 seconds

          if (loginAttempts.current >= 10 && loginAttempts.current < 20) {
            timeoutDuration = 300000; // 5 minutes for 10-19 attempts
          } else if (loginAttempts.current >= 20) {
            timeoutDuration = 600000; // 10 minutes for 20 or more attempts
          }

          const timeoutSeconds = timeoutDuration / 1000;

          console.error(`Too many login attempts. Blocking login for ${timeoutSeconds} seconds.`);
          setLoginBlocked(true);
          setTimeoutMessage(`Please try again in: ${timeoutSeconds} seconds.`);

          setTimeout(() => {
            setLoginBlocked(false);
            loginAttempts.current = 0;
            setTimeoutMessage(""); // Clear timeout message after the timeout period
          }, timeoutDuration);
        }
      }
    }
  };

  return (
    <div className="login-container" style={{ backgroundImage: `url(${background})` }}>
      <div className="logo-container">
        <img src={logo} alt="Logo" className="logo" />
      </div>
      <form className="login-form" onSubmit={(e) => { e.preventDefault(); initiateLogin(); }}>
        <label htmlFor="email">Email</label>
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <label htmlFor="password">Password</label>
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        <button type="submit">Sign In</button>
        {loginError && <p className="error-message">{loginError}</p>}
        {timeoutMessage && <p className="timeout-message">{timeoutMessage}</p>}
      </form>
    </div>
  );
};

export default Login;
